import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { TASK_QUERY, PEOPLE_QUERY } from '../../graphql/tasks';
import LoadingOverlay from '../common/LoadingOverlay';
import TaskProgressSection from './TaskProgressSection';

const TaskViewModal = ({ open, onClose, taskId }) => {
  const { data, loading } = useQuery(TASK_QUERY, {
    variables: { id: taskId },
    skip: !open || !taskId,
    fetchPolicy: 'network-only'
  });

  const { data: peopleData } = useQuery(PEOPLE_QUERY, {
    variables: { 
      page: 1, 
      size: 10000,
      filters: { active: "true" }
    },
    fetchPolicy: 'cache-first'
  });

  const task = data?.task;
  const people = peopleData?.people?.list || [];

  const getUserName = (userId) => {
    const user = people.find(p => p.id === userId?.toString());
    return user ? `${user.last_name} ${user.first_name}` : userId || '-';
  };

  const getPriorityLabel = (priority) => {
    const labels = ['Highest', 'High', 'Normal', 'Low', 'Lowest'];
    return labels[priority] || 'Normal';
  };

  const getPriorityColor = (priority) => {
    const colors = ['error', 'warning', 'info', 'success', 'default'];
    return colors[priority] || 'info';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const getCompleteStatus = (complete) => {
    if (complete === 0) return { label: 'New', color: 'default' };
    if (complete === 100) return { label: 'Done', color: 'success' };
    return { label: 'In Progress', color: 'primary' };
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!task) {
    return null;
  }

  const project = task?.project;
  const projectName = project?.name || '-';
  
  const element = task?.element;
  const elementName = element?.name || '-';

  const completeStatus = getCompleteStatus(task.complete);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {task.name}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="text.secondary">
            {projectName} / {elementName}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Status & Progress */}
          <Grid item xs={12} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Status & Progress</Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Chip label={completeStatus.label} color={completeStatus.color} size="small" />
                    <Typography variant="body1" fontWeight="bold">{task.complete}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={task.complete} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: task.complete === 100 ? 'success.main' : 'primary.main'
                      }
                    }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Priority:</Typography>
                  <Chip label={getPriorityLabel(task.priority)} color={getPriorityColor(task.priority)} size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Schedule */}
          <Grid item xs={12} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Schedule</Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Start</Typography>
                  <Typography variant="body2">{formatDate(task.start_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">End</Typography>
                  <Typography variant="body2">{formatDate(task.end_date)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Task Types */}
          <Grid item xs={12} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Task Types</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {task.task_type?.map((type, index) => (
                    <Chip key={index} label={type.name} variant="outlined" size="small" color="primary" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Companies */}
          <Grid item xs={12} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Companies</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {task.companies?.map((company, index) => (
                    <Chip key={index} label={company.name} size="small" variant="outlined" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* People */}
          <Grid item xs={12} md={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Responsible</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {task.responsible?.map((person, index) => (
                    <Chip key={index} label={`${person.last_name} ${person.first_name} (${person.uuid})`} size="small" variant="filled" color="primary" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Description */}
          {task.description && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{task.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Attachments */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Attachments</Typography>
                {task.attachments?.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {task.attachments.map((attachment, index) => (
                      <Chip 
                        key={index}
                        label={attachment.data_file_name}
                        variant="outlined"
                        size="small"
                        color="info"
                        onClick={() => window.open(attachment.url, '_blank')}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No attachments</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Task Progress */}
          <TaskProgressSection taskProgress={task.task_progress} getUserName={getUserName} />

          {/* Metadata */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Metadata</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      Created by <Typography component="span" fontWeight="bold">{getUserName(task.created_by)}</Typography> at <Typography component="span" fontWeight="bold">{formatDate(task.created_at)}</Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      Updated by <Typography component="span" fontWeight="bold">{getUserName(task.updated_by)}</Typography> at <Typography component="span" fontWeight="bold">{formatDate(task.updated_at)}</Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default TaskViewModal;
